import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Avatar, PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../../components/ui';
import RichTextEditor from '../../components/RichTextEditor';
import NotificationComposer from '../../components/NotificationComposer';
import Modal from '../../components/Modal';
import AssociationTabs from '../../components/AssociationTabs';
import ColorField, { cssBackground } from '../../components/ColorField';
import IconColorField from '../../components/IconColorField';
import PriceField from '../../components/PriceField';
import AnnounceField from '../../components/AnnounceField';
import StatusPill from '../../components/StatusPill';
import BackCrumb, { useBackTarget } from '../../components/BackCrumb';
import { handleHasMembersError } from '../../components/RemoveMember';
import { CoursePreviewBody } from '../LearnCoursePage';
import '../../styles/learn.css';

const COLORS = ['indigo', 'teal', 'wpl', 'violet', 'pink', 'amber'];

export default function CourseLessonsAdminPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [savingCourse, setSavingCourse] = useState(false);
  const [meta, setMeta] = useState(null); // editable course fields
  const { loading, error, data, reload } = useAsync(async () => {
    const [courses, lessons] = await Promise.all([
      api.adminListCourses().catch(() => []),
      api.adminListLessons(id).catch(() => []),
    ]);
    const course = (Array.isArray(courses) ? courses : []).find((c) => c.id === id) || null;
    return { course, lessons: Array.isArray(lessons) ? lessons : [] };
  }, [id]);

  const toMeta = (c) => ({
    title: c.title || '', summary: c.summary || '',
    tier: c.tier || 'FREE', coverColor: c.coverColor || 'teal', iconColor: c.iconColor || '',
    brand: c.brand || 'compassionpractice',
    priceCents: c.priceCents || 0, currency: c.currency || 'usd',
    announce: c.announce !== false,
  });

  // Hydrate the editable course header once loaded.
  useEffect(() => {
    if (data?.course) setMeta(toMeta(data.course));
  }, [data?.course]);

  // `?created=1` — set by "+ New course". A never-saved draft is deleted on
  // Cancel; the first successful save clears the marker.
  const [params, setParams] = useSearchParams();
  const back = useBackTarget({ fallback: { path: '/admin/courses', label: 'Manage courses' } });
  const justCreated = params.get('created') === '1';
  const clearCreated = () => {
    if (!justCreated) return;
    const p = new URLSearchParams(params); p.delete('created');
    setParams(p, { replace: true });
  };

  const dirty = !!(meta && data?.course && JSON.stringify(meta) !== JSON.stringify(toMeta(data.course)));
  const cancel = async () => {
    if (justCreated) {
      if ((dirty || data?.lessons?.length) && !window.confirm('Discard this new course?')) return;
      setSavingCourse(true);
      try { await api.adminDeleteCourse(id); } catch (e) { alert(e.message); setSavingCourse(false); return; }
      navigate(back.path);
      return;
    }
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    navigate(back.path);
  };

  const setM = (k) => (e) => setMeta((m) => ({ ...m, [k]: e.target.value }));

  // Access follows price: $0 → FREE, >$0 → PRO. Currency is always USD.
  const payload = (extra = {}) => ({
    ...meta,
    tier: (Number(meta.priceCents) || 0) > 0 ? 'PRO' : 'FREE',
    currency: 'usd',
    ...extra,
  });

  // Only the author (or superadmin) can delete; the server refuses while
  // anyone is enrolled (HAS_MEMBERS → offer Manage members).
  const deleteCourse = async () => {
    if (!window.confirm(`Delete "${meta?.title || 'this course'}" and all its lessons? This cannot be undone.`)) return;
    try { await api.adminDeleteCourse(id); navigate('/admin/courses'); }
    catch (e) { if (!handleHasMembersError(e, 'courses', id, navigate)) alert(e?.data?.message || e.message); }
  };

  const saveCourse = async () => {
    if (!meta?.title?.trim()) { alert('Title is required.'); return; }
    setSavingCourse(true);
    try { await api.adminUpdateCourse(id, payload()); clearCreated(); reload(); alert('Saved.'); }
    catch (e) { alert(e.message); }
    finally { setSavingCourse(false); }
  };

  // Publish/unpublish from the editor (also available on the Courses list).
  // Saves the current details alongside the status flip so edits aren't lost.
  const togglePublish = async () => {
    if (!meta?.title?.trim()) { alert('Title is required.'); return; }
    const next = data?.course?.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    if (next === 'DRAFT' && !window.confirm('Unpublish? It will be hidden from members until you publish it again.')) return;
    setSavingCourse(true);
    try { await api.adminUpdateCourse(id, payload({ status: next })); clearCreated(); reload(); }
    catch (e) { if (!handleHasMembersError(e, 'courses', id, navigate)) alert(e?.data?.message || e.message); }
    finally { setSavingCourse(false); }
  };

  // Preview: save course details silently, then show the course as the reader
  // (Library) renders it, in a closable Web/Phone modal — no navigation.
  const [previewCourse, setPreviewCourse] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('web'); // 'web' | 'phone'
  const openPreview = async () => {
    if (!meta?.title?.trim()) { alert('Title is required.'); return; }
    try { await api.adminUpdateCourse(id, payload()); clearCreated(); }
    catch (e) { alert(e.message); return; }
    setPreviewDevice('web');
    setPreviewing(true);
    try { setPreviewCourse(await api.getLearnCourse(id)); }
    catch (e) { alert(e.message); setPreviewing(false); }
  };

  // Icon image upload (shown in the tile instead of the icon color).
  const coverRef = useRef(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverVer, setCoverVer] = useState(0); // cache-buster after replace
  const onCoverFile = async (e) => {
    const file = e.target.files?.[0]; if (e.target) e.target.value = '';
    if (!file) return;
    setCoverBusy(true);
    try { await api.uploadCourseCover(id, file); setCoverVer((v) => v + 1); reload(); }
    catch (err) { alert(err.message); }
    finally { setCoverBusy(false); }
  };
  const removeCover = async () => {
    if (!window.confirm('Remove the cover image (revert to the color)?')) return;
    setCoverBusy(true);
    try { await api.deleteCourseCover(id); reload(); }
    catch (err) { alert(err.message); }
    finally { setCoverBusy(false); }
  };

  // Create a draft lesson immediately and open its editor — no popup.
  const newLesson = async () => {
    setCreating(true);
    try {
      const lesson = await api.adminCreateLesson(id, { title: 'Untitled lesson', tier: 'FREE', status: 'DRAFT' });
      navigate(`/admin/content/lesson/${lesson.id}`);
    } catch (e) { alert(e.message); setCreating(false); }
  };

  const del = async (l) => {
    if (!window.confirm(`Delete lesson "${l.title}"?`)) return;
    try { await api.adminDeleteLesson(l.id); reload(); } catch (e) { alert(e.message); }
  };

  // Reorder: swap a lesson with its neighbour and persist the new order.
  const move = async (index, dir) => {
    const ls = [...data.lessons];
    const j = index + dir;
    if (j < 0 || j >= ls.length) return;
    [ls[index], ls[j]] = [ls[j], ls[index]];
    try { await api.adminReorderLessons(id, ls.map((l) => l.id)); reload(); }
    catch (e) { alert(e.message); }
  };

  // Spinner only on the first load; reloads keep the editor mounted.
  if ((loading && !data) || meta == null) return <div className="page"><Spinner /></div>;
  if (error) return <div className="page"><ErrorNote error={error} onRetry={reload} /></div>;

  return (
    <div className="page">
      <PageHead
        breadcrumb={<BackCrumb to={back} />}
        title={meta.title || 'Course'}
        sub="Edit the course details and its lessons"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <StatusPill draft={data?.course?.status !== 'PUBLISHED'} />
            <button className="btn" disabled={savingCourse} onClick={cancel}>Cancel</button>
            <button className="btn" disabled={savingCourse} onClick={saveCourse}>{savingCourse ? 'Saving…' : 'Save'}</button>
            <button className="btn" disabled={savingCourse} onClick={openPreview}>Preview</button>
            <button className={`btn ${data?.course?.status === 'PUBLISHED' ? '' : 'btn-primary'}`}
                    disabled={savingCourse} onClick={togglePublish}>
              {data?.course?.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
            </button>
          </div>
        }
      />

      {previewing && (
        <Modal title="Preview — how learners see this course" width="min(1040px, 94vw)"
               onClose={() => { setPreviewing(false); setPreviewCourse(null); }}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 14 }}>
            {['web', 'phone'].map((d) => (
              <button key={d} className={`btn btn-sm ${previewDevice === d ? 'btn-primary' : ''}`} onClick={() => setPreviewDevice(d)}>
                {d === 'web' ? '🖥 Web' : '📱 Phone'}
              </button>
            ))}
          </div>
          {previewCourse
            ? (previewDevice === 'phone' ? (
                <div style={{ width: 390, maxWidth: '100%', margin: '0 auto', border: '1px solid var(--border)', borderRadius: 24, overflow: 'hidden', boxShadow: 'var(--shadow-card)', background: 'var(--bg, #f6f7fb)' }}>
                  <div className="page" style={{ padding: 12 }}><CoursePreviewBody course={previewCourse} /></div>
                </div>
              ) : (
                <div className="page" style={{ padding: 0 }}><CoursePreviewBody course={previewCourse} /></div>
              ))
            : <Spinner />}
        </Modal>
      )}

      <Section title="Course details">
        <div className="form-field"><label>Cover color</label>
          <ColorField value={meta.coverColor} onChange={(hex) => setMeta((m) => ({ ...m, coverColor: hex }))} /></div>
        <IconColorField value={meta.iconColor} coverValue={meta.coverColor}
          onChange={(v) => setMeta((m) => ({ ...m, iconColor: v }))} />
        <div className="form-field">
          <label>Icon image (optional — shown instead of the icon color)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Avatar name={meta.title} colorSeed={id} square size={48}
              src={data.course?.coverImageUri ? `${api.courseCoverUrl(id)}?v=${coverVer}` : null}
              bg={cssBackground(meta.iconColor || meta.coverColor)} />
            <input ref={coverRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={onCoverFile} />
            <button type="button" className="btn btn-sm" disabled={coverBusy} onClick={() => coverRef.current?.click()}>
              {coverBusy ? 'Uploading…' : (data.course?.coverImageUri ? 'Replace image' : 'Upload image')}
            </button>
            {data.course?.coverImageUri && (
              <button type="button" className="btn btn-sm btn-danger" disabled={coverBusy} onClick={removeCover}>Remove</button>
            )}
          </div>
        </div>
        {/* Price (USD only) drives access: $0 = free self-enroll, >0 = paid.
            Tier is derived from it on save — no separate Access picker. */}
        <div className="form-field"><label>Title <span className="req">*</span></label>
          <input className="input" value={meta.title} onChange={setM('title')} /></div>
        <div className="form-field"><label>Summary</label>
          <RichTextEditor value={meta.summary} onChange={(html) => setMeta((m) => ({ ...m, summary: html }))} placeholder="Short course summary…" /></div>
        <PriceField cents={meta.priceCents}
          onChangeCents={(v) => setMeta((m) => ({ ...m, priceCents: v }))}
          freeHint="$0 = free (self-enroll)." />
        <AnnounceField kind="course" announce={meta.announce} announcedAt={data.course.announcedAt}
          onChange={(v) => setMeta((m) => ({ ...m, announce: v }))} />
      </Section>

      <AssociationTabs sourceKind="course" sourceId={id} />

      <Section style={{ padding: 0 }}>
        <div className="list-toolbar">
          <div className="section-title">Lessons</div>
          <button className="btn btn-sm btn-primary" disabled={creating} onClick={newLesson}>{creating ? 'Creating…' : '+ New lesson'}</button>
        </div>
        {data.lessons.length === 0 ? <div style={{ padding: 18 }}><Empty>No lessons yet.</Empty></div> : (
          <table className="tbl">
            <thead><tr><th style={{ width: 40 }}>#</th><th>Lesson</th><th>Access</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {data.lessons.map((l, i) => (
                <tr key={l.id} className="clickable" style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/content/lesson/${l.id}`)}>
                  <td style={{ color: 'var(--muted)', fontWeight: 600 }}>{i + 1}</td>
                  <td><b>{l.title}</b>{l.subtitle ? <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{l.subtitle}</div> : null}</td>
                  <td>{l.tier === 'PRO' ? <span className="pill pro">PRO</span> : <span className="pill free">Free</span>}</td>
                  <td><span className="pill" style={{ background: l.status === 'PUBLISHED' ? 'var(--green-bg)' : '#f3f4f6', color: l.status === 'PUBLISHED' ? 'var(--green-text)' : 'var(--muted)' }}>{l.status}</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <button className="btn btn-sm" disabled={i === 0} title="Move up" onClick={() => move(i, -1)}>↑</button>
                    <button className="btn btn-sm" style={{ marginLeft: 6 }} disabled={i === data.lessons.length - 1} title="Move down" onClick={() => move(i, 1)}>↓</button>
                    <button className="btn btn-sm btn-danger" style={{ marginLeft: 6 }} onClick={() => del(l)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <NotificationComposer scopeType="COURSE" scopeId={id} scopeName={meta.title || 'this course'} />

      {!justCreated && (
        <Section title="Delete course">
          <div className="danger-zone">
            <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: '0.875rem' }}>
              Deletes the course and its lessons. Anyone enrolled must be removed first in Manage members.
            </p>
            <button className="btn btn-danger" onClick={deleteCourse}>Delete course</button>
          </div>
        </Section>
      )}
    </div>
  );
}
